/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The card the Real Estate record screens put their sections in.
 *
 * Legacy: components/detail/InfoCard, unchanged. UI v2: the v2 record section
 * (components/v2/record DetailSection): a lucide icon, the title in the display
 * font, a hairline under the header and the actions on the right.
 *
 * components/detail/InfoCard itself is not touched; the call center uses it.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Activity, ArrowLeftRight, BadgeCheck, Building2, Calculator, CalendarDays, Eye, FileText, Handshake, HardHat, History,
  Images, KeyRound, Landmark, ListChecks, ListOrdered, Pencil, PieChart, Receipt, ScrollText, SlidersHorizontal, User, Users, Wallet,
} from 'lucide-react';
import InfoCard from 'components/detail/InfoCard';
import { DetailSection } from 'components/v2/record/DetailSection';
import { useIsReV2 } from '../../v2/parts';

/** Material icon names the RE screens use → lucide. */
export const RE_CARD_ICONS = {
  groups: Users,
  people: Users,
  apartment: Building2,
  account_balance_wallet: Wallet,
  payments: Wallet,
  event_note: CalendarDays,
  handshake: Handshake,
  swap_horiz: ArrowLeftRight,
  description: FileText,
  verified: BadgeCheck,
  receipt: Receipt,
  receipt_long: Receipt,
  donut_small: PieChart,
  edit: Pencil,
  account_balance: Landmark,
  checklist: ListChecks,
  rule: ListChecks,
  construction: HardHat,
  photo_library: Images,
  sticky_note_2: ScrollText,
  notes: ScrollText,
  calculate: Calculator,
  history: History,
  key: KeyRound,
  person: User,
  preview: Eye,
  timeline: Activity,
  tune: SlidersHorizontal,
  format_list_numbered: ListOrdered,
};

export default function ReInfoCard({ icon, title, headerAction, children, minHeight }) {
  const isV2 = useIsReV2();
  if (!isV2) {
    return (
      <InfoCard icon={icon} title={title} headerAction={headerAction} minHeight={minHeight}>
        {children}
      </InfoCard>
    );
  }
  return (
    <DetailSection
      title={title}
      // Unmapped names fall back to DetailSection's icon-by-title; no icon stays none.
      icon={icon ? RE_CARD_ICONS[icon] : null}
      actions={headerAction}
      sx={{ height: '100%', mb: 0 }}
    >
      {minHeight !== undefined ? <div style={{ minHeight }}>{children}</div> : children}
    </DetailSection>
  );
}

ReInfoCard.propTypes = {
  icon: PropTypes.string,
  title: PropTypes.node.isRequired,
  headerAction: PropTypes.node,
  children: PropTypes.node,
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ReInfoCard.defaultProps = { icon: null, headerAction: null, children: null, minHeight: undefined };
